import React, { useState, useContext, useEffect, useRef } from "react";
import { StyleSheet, TouchableOpacity, Text, Modal, TouchableWithoutFeedback, ViewStyle } from "react-native";
import { Avatar } from "react-native-paper";
import Icons from "@expo/vector-icons/MaterialCommunityIcons";
import { View } from "../shared/Themed";
import AvatarMenu from "./AvatarMenu";
import NotificationMenu from "./NotificationMenu";
import { useDispatch, useSelector } from "react-redux";
import DeviceOrientationContext from "../context/DeviceOrientationContext";
import { ConfigurationDispatcher } from "../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../redux/constants/actionTypesConfigurations";
import { getAllNotificationsCount } from "../../redux/dispatcher/notificationDispatcher";
import { useAppTheme } from "../../hooks/useAppTheme";
import type { ThemeColors } from "../../utils/constants/colors";

export default function UserAvatar() {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const [isVisible] = useState<boolean>(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState<boolean>(false);
  const notificationButtonRef = useRef(null);
  const { loading, notificationCount } = useSelector(
    (state: any) => state.notification
  );
  const dispatch = useDispatch<any>();
  const orientationContext = useContext(DeviceOrientationContext);
  // Redux state selection
  const { userInfo, hasAvatarMenu, hasAvatar, isDrawer, profilePhoto, navigation }: any =
    useSelector((state: any) => state.configurations);
  const setHasAvatarMenu = (data: boolean) => {
    ConfigurationDispatcher(actionTypesConfigurations.HasAvatarMenu, data);
  };
  const avatarButtonRef = useRef(null);

  const displayName = userInfo?.display_name && userInfo.display_name !== "undefined" ? userInfo.display_name : null;
  const fullName: string[] = displayName?.split(" ") ?? [];
  const initial = fullName.length > 0 ? fullName[0].charAt(0) : "U";
  const firstName = fullName.length > 0 ? fullName[0] : "User";

  useEffect(() => {
    dispatch(getAllNotificationsCount());
  }, []);

  return (
    <View
      style={[
        styles.container,
      ]}
    >
      <View style={styles.topBarRow}>
        {/* Notification Bell */}
        <TouchableOpacity
          ref={notificationButtonRef}
          style={styles.notificationButton}
          onPress={() => setIsNotificationMenuOpen(!isNotificationMenuOpen)}
        >
          <Icons name="bell-outline" size={22} color={colors.textSecondary} />
          {!loading && notificationCount > 0 && (
            <View style={styles.notifBadge}>
              <Text style={styles.notifBadgeText}>{notificationCount}</Text>
            </View>
          )}
        </TouchableOpacity>

        {/* Vertical Divider */}
        <View style={styles.divider} />

        {/* Avatar + Name + Dropdown */}
        <TouchableOpacity
          ref={avatarButtonRef}
          style={styles.avatarButton}
          onPress={() => setHasAvatarMenu(!hasAvatarMenu)}
        >
          <View style={styles.avatarWrapper}>
            {hasAvatar ? (
              <Avatar.Image
                size={isDrawer ? 55 : 32}
                source={profilePhoto}
                style={{ backgroundColor: colors.surface }}
              />
            ) : (
              <Avatar.Text
                size={isDrawer ? 55 : 32}
                label={initial}
                style={{ backgroundColor: "#4f46e5" }}
                labelStyle={{ fontSize: 13, fontWeight: "700" }}
              />
            )}
          </View>

          {!orientationContext.isSmallDevice && (
            <>
              <Text style={styles.userName}>{firstName}</Text>
              <Icons
                name={hasAvatarMenu ? "chevron-up" : "chevron-down"}
                size={18}
                color={colors.textSecondary}
                style={{ marginLeft: 2 }}
              />
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* Notification Dropdown Menu */}
      {isNotificationMenuOpen && !isDrawer && !orientationContext.isSmallDevice ? (
        <View
          style={[
            styles.notificationMenuPosition,
            styles.shadowProp,
          ]}
        >
          <NotificationMenu
            onClose={() => setIsNotificationMenuOpen(false)}
            navigation={navigation}
            buttonRef={notificationButtonRef}
          />
        </View>
      ) : null}

      {/* Avatar Dropdown Menu via Modal */}
      {hasAvatarMenu && !isDrawer && !orientationContext.isSmallDevice ? (
        <View
          style={[
            styles.avatarMenu,
            styles.shadowProp,
            {
              width: orientationContext.isSmallDevice ? 200 : 280,
            },
          ]}
        >
          <AvatarMenu buttonRef={avatarButtonRef} />
        </View>
      ) : null}
    </View>
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      position: "absolute",
      right: 16,
      top: 0,
      flexDirection: "column",
      marginTop: 15,
      zIndex: 9999,
      overflow: "visible",
      elevation: 100,
    },
    topBarRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    // Notification bell
    notificationButton: {
      position: "relative",
      padding: 6,
      marginRight: 8,
      borderRadius: 8,
    },
    divider: {
      width: 1,
      height: 24,
      backgroundColor: colors.textMuted,
      marginHorizontal: 8,
    },
    notifBadge: {
      position: "absolute",
      top: 2,
      right: 2,
      backgroundColor: colors.danger,
      borderRadius: 8,
      paddingHorizontal: 4,
      minWidth: 16,
      height: 16,
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10,
      borderWidth: 2,
      borderColor: "#ffffff",
    },
    notifBadgeText: {
      color: "#ffffff",
      fontSize: 9,
      fontWeight: "700",
      textAlign: "center",
    },
    // Avatar button
    avatarButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 4,
      paddingHorizontal: 6,
      borderRadius: 20,
    },
    avatarWrapper: {
      position: "relative",
      alignItems: "center",
      justifyContent: "center",
    },
    userName: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
      marginLeft: 8,
      letterSpacing: 0.1,
    },
    // Dropdown
    avatar: {
      justifyContent: "space-around",
      overflow: "visible",
    },
    modalOverlay: {
      flex: 1,
    } as ViewStyle,
    notificationMenuPosition: {
      position: "absolute",
      top: 60,
      right: 60,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      backgroundColor: colors.surface,
      zIndex: 9999,
    },
    avatarMenu: {
      textAlign: "left",
      position: "fixed" as any,
      top: 60,
      right: 16,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 12,
      backgroundColor: colors.surface,
      zIndex: 9999,
    },
    shadowProp: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.12,
      shadowRadius: 24,
    },
  });
}
